import React from 'react';
import { Form, Input, Button, message, Progress } from 'antd';
import {
    UserOutlined,
    LockOutlined,
    PhoneOutlined,
    MailOutlined,
    UserAddOutlined,
} from '@ant-design/icons';
import { Link, Navigate } from 'react-router-dom';
import { register } from '../../api/sys_user';
import { isAuthenticated, getUser } from '../../utils/auth';
import { canManageUsers, isNormalUser } from '../../utils/role';
import { loginIdFormValidator, LOGIN_ID_RULE_HINT } from '../../utils/loginIdPolicy';
import {
    passwordRequiredValidator,
    PASSWORD_RULE_HINT,
    scorePasswordStrength,
} from '../../utils/passwordPolicy';
import AuthShell from '../AuthShell/AuthShell';

class RegisterPage extends React.Component {
    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.state = {
            loading: false,
            redirectTo: null,
            passwordStrength: 0,
        };
    }

    componentDidMount() {
        if (isAuthenticated()) {
            const u = getUser();
            this.setState({
                redirectTo: canManageUsers(u)
                    ? '/admin/users'
                    : isNormalUser(u)
                      ? '/admin/videos'
                      : '/admin/videos',
            });
        }
    }

    onPasswordChange = (e) => {
        this.setState({ passwordStrength: scorePasswordStrength(e.target.value) });
    };

    strengthStatus = () => {
        const { passwordStrength } = this.state;
        if (passwordStrength < 40) {
            return { status: 'exception', text: '弱' };
        }
        if (passwordStrength < 75) {
            return { status: 'normal', text: '中' };
        }
        return { status: 'success', text: '强' };
    };

    onFinish = async (values) => {
        this.setState({ loading: true });
        try {
            await register({
                loginId: values.loginId,
                password: values.password,
                email: values.email || undefined,
                phone: values.phone || undefined,
            });
            message.success('注册成功，请登录');
            this.setState({ redirectTo: '/login' });
        } catch (error) {
            message.error(error.message || '注册失败');
        } finally {
            this.setState({ loading: false });
        }
    };

    render() {
        const { loading, redirectTo, passwordStrength } = this.state;
        if (redirectTo) {
            return <Navigate to={redirectTo} replace />;
        }

        const strength = this.strengthStatus();

        return (
            <AuthShell title="创建账号" subtitle="填写信息完成注册">
                <Form
                    ref={this.formRef}
                    name="register"
                    onFinish={this.onFinish}
                    layout="vertical"
                    size="large"
                >
                    <Form.Item
                        name="loginId"
                        label="登录名"
                        extra={LOGIN_ID_RULE_HINT}
                        rules={[{ validator: loginIdFormValidator() }]}
                    >
                        <Input prefix={<UserOutlined />} placeholder="如 myuser_01" allowClear />
                    </Form.Item>
                    <Form.Item
                        name="password"
                        label="密码"
                        extra={PASSWORD_RULE_HINT}
                        rules={[
                            {
                                validator: passwordRequiredValidator(() =>
                                    this.formRef.current?.getFieldValue('loginId')
                                ),
                            },
                        ]}
                    >
                        <Input.Password
                            prefix={<LockOutlined />}
                            placeholder="设置登录密码"
                            onChange={this.onPasswordChange}
                        />
                    </Form.Item>
                    {passwordStrength > 0 && (
                        <Form.Item style={{ marginTop: -12 }}>
                            <Progress
                                percent={passwordStrength}
                                size="small"
                                status={strength.status}
                                format={() => strength.text}
                            />
                        </Form.Item>
                    )}
                    <Form.Item
                        name="email"
                        label="邮箱"
                        rules={[{ type: 'email', message: '请输入有效的邮箱' }]}
                    >
                        <Input prefix={<MailOutlined />} placeholder="选填" allowClear />
                    </Form.Item>
                    <Form.Item
                        name="phone"
                        label="手机号"
                        rules={[
                            {
                                pattern: /^$|^1[3-9]\d{9}$/,
                                message: '请输入有效的 11 位手机号',
                            },
                        ]}
                    >
                        <Input prefix={<PhoneOutlined />} placeholder="选填" maxLength={11} allowClear />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 12 }}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            className="auth-btn-primary"
                            block
                            loading={loading}
                            icon={<UserAddOutlined />}
                        >
                            注 册
                        </Button>
                    </Form.Item>
                    <div className="auth-footer">
                        <Link to="/login" className="auth-link">
                            已有账号？返回登录
                        </Link>
                    </div>
                </Form>
            </AuthShell>
        );
    }
}

export default RegisterPage;
